/** PDF-only image hydration. Keep the caller's commercial snapshot intact. */
interface PhotoLine {
  lineItemId?: string;
  lineKey?: string;
  rowId?: string;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
}
interface PhotoEstimate {
  id?: string;
  lineItems?: readonly PhotoLine[];
}
interface PhotoOption {
  optionKey?: string;
  label?: string;
  lineItems?: readonly PhotoLine[];
}
interface PhotoProposal {
  id?: string;
  options: readonly PhotoOption[];
}
export interface PdfImageReadOptions {
  audience?: 'staff' | 'public' | 'portal';
  opsBaseUrl?: string;
  id?: string;
  draft?: boolean;
}

export const PDF_LINE_IMAGE_QUERY = 'includeLineItemImageDataUri=true';
const INLINE_IMAGE = /^data:image\/(?:png|jpeg);base64,/i;
// Drafts hydrate through separate catalog requests, so their combined inline
// output needs the same encoded budget as a saved document's server read.
const DRAFT_PDF_IMAGE_BYTES = 4 * 1024 * 1024;
const inlinePhoto = (line: PhotoLine) =>
  [line.imagePreviewUrl, line.imageUrl].find((value) => value && INLINE_IMAGE.test(value)) ?? null;
const needsPhoto = (line: PhotoLine) =>
  Boolean((line.imageUrl || line.imagePreviewUrl) && !inlinePhoto(line));

function matchLine(line: PhotoLine, lines: readonly PhotoLine[], original: readonly PhotoLine[]) {
  const field = line.lineKey ? 'lineKey' : line.rowId ? 'rowId' : 'lineItemId';
  const key = line[field];
  if (!key || original.filter((entry) => entry[field] === key).length !== 1) return undefined;
  const matches = lines.filter(
    (entry) => entry[field] === key && entry.lineItemId === line.lineItemId,
  );
  return matches.length === 1 ? matches[0] : undefined;
}

/** Copy only inline media; never prices, selections, names or approval/signature fields. */
export function mergePdfLineImages<T extends PhotoLine>(
  lines: readonly T[],
  source: readonly PhotoLine[],
): T[] {
  return lines.map((line) => {
    const match = matchLine(line, source, lines);
    const sameReference = !line.imageUrl || match?.imageUrl === line.imageUrl;
    const image = inlinePhoto(line) ?? (match && sameReference ? inlinePhoto(match) : null);
    if (image) return { ...line, imagePreviewUrl: image };
    // The PDF renderer must not fetch a raw key, expired URL or unsupported codec.
    return line.imageUrl || line.imagePreviewUrl
      ? { ...line, imageUrl: undefined, imagePreviewUrl: undefined }
      : line;
  });
}

export function mergeEstimatePdfImages<T extends PhotoEstimate>(
  estimate: T,
  source: PhotoEstimate,
): T {
  return {
    ...estimate,
    lineItems: mergePdfLineImages(estimate.lineItems ?? [], source.lineItems ?? []),
  };
}

async function readDocument(
  kind: 'estimates' | 'proposals',
  id: string,
  options: PdfImageReadOptions,
): Promise<unknown> {
  const audience = options.audience ?? 'public';
  const encoded = encodeURIComponent(id);
  if (audience === 'staff') {
    const { apiGet, operationsClient } = await import('@/lib/api');
    return apiGet(operationsClient, `/${kind}/${encoded}?${PDF_LINE_IMAGE_QUERY}`, {
      timeout: 5000,
    });
  }
  if (audience === 'portal') {
    const { customerClient } = await import('@/lib/portal/customerClient');
    return (
      await customerClient.get(`/api/portal/${kind}/${encoded}?${PDF_LINE_IMAGE_QUERY}`, {
        timeout: 5000,
      })
    ).data;
  }
  const response = await fetch(
    `${options.opsBaseUrl ?? import.meta.env.VITE_OPERATIONS_URL ?? ''}/${kind}/public/${encoded}?${PDF_LINE_IMAGE_QUERY}`,
    {
      signal: AbortSignal.timeout(5000),
    },
  );
  if (!response.ok) throw new Error('Could not prepare document photos');
  return response.json();
}

async function draftPhotos(lines: readonly PhotoLine[]): Promise<PhotoLine[]> {
  const { apiGet, operationsClient } = await import('@/lib/api');
  const { operationsPricebookEndpoints } = await import('@/lib/api/endpoints');
  const pendingLines = lines.filter((line) => needsPhoto(line) && line.imageUrl);
  const ids = [
    ...new Set(
      pendingLines.map((line) => line.lineItemId).filter((id): id is string => Boolean(id)),
    ),
  ];
  const photos = new Map<string, PhotoLine>();
  let remainingBytes = DRAFT_PDF_IMAGE_BYTES;
  let budgetClosed = false;
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(4, ids.length) }, async () => {
      while (!budgetClosed && cursor < ids.length) {
        const id = ids[cursor++];
        const photo = await apiGet<PhotoLine>(
          operationsClient,
          `${operationsPricebookEndpoints.detail(id)}?${PDF_LINE_IMAGE_QUERY}`,
          { timeout: 5000 },
        ).catch(() => null);
        // Other workers may finish after the budget is exhausted. Discard
        // those bodies rather than retaining them in the hydration map.
        if (budgetClosed || !photo) continue;
        const image = inlinePhoto(photo);
        if (!image) continue;
        const copies = pendingLines.filter(
          (line) => line.lineItemId === id && line.imageUrl === photo.imageUrl,
        ).length;
        if (copies === 0) continue;
        const bytes = image.length * copies;
        if (bytes > remainingBytes) {
          budgetClosed = true;
          continue;
        }
        photos.set(id, { imageUrl: photo.imageUrl, imagePreviewUrl: image });
        remainingBytes -= bytes;
        budgetClosed = remainingBytes === 0;
      }
    }),
  );
  return lines.map((line) => {
    if (!needsPhoto(line) || !line.lineItemId || !line.imageUrl) return line;
    const current = photos.get(line.lineItemId);
    // Drafts can use the catalog only for the exact image already selected.
    return current?.imageUrl === line.imageUrl
      ? { ...line, imagePreviewUrl: inlinePhoto(current) }
      : { ...line, imagePreviewUrl: undefined };
  });
}

export async function prepareEstimatePdfImages<T extends PhotoEstimate>(
  estimate: T,
  options: PdfImageReadOptions = {},
): Promise<T> {
  const lines = estimate.lineItems ?? [];
  if (!lines.some(needsPhoto)) return mergeEstimatePdfImages(estimate, estimate);
  try {
    const id = options.id ?? estimate.id;
    if (options.draft || !id || id.startsWith('draft-')) {
      const source = options.audience === 'staff' ? await draftPhotos(lines) : [];
      return mergeEstimatePdfImages(estimate, { lineItems: source });
    }
    const response = (await readDocument('estimates', id, options)) as { estimate?: PhotoEstimate };
    return mergeEstimatePdfImages(estimate, response.estimate ?? {});
  } catch {
    return mergeEstimatePdfImages(estimate, {});
  }
}

export async function prepareProposalPdfImages<T extends PhotoProposal>(
  proposal: T,
  options: PdfImageReadOptions = {},
): Promise<T> {
  if (!proposal.options.some((option) => option.lineItems?.some(needsPhoto))) return proposal;
  let source: PhotoProposal | undefined;
  try {
    const id = options.id ?? proposal.id;
    if (id) source = (await readDocument('proposals', id, options)) as PhotoProposal;
  } catch {
    /* A photo failure must never prevent the signed record. */
  }
  return mergeProposalPdfImages(proposal, source);
}

export function mergeProposalPdfImages<T extends PhotoProposal>(
  proposal: T,
  source?: PhotoProposal,
): T {
  return {
    ...proposal,
    options: proposal.options.map((option, index) => {
      const matches = option.optionKey
        ? (source?.options?.filter((entry) => entry.optionKey === option.optionKey) ?? [])
        : source?.options?.length === proposal.options.length &&
            !source.options[index]?.optionKey &&
            source.options[index]?.label === option.label
          ? [source.options[index]]
          : [];
      return {
        ...option,
        lineItems: mergePdfLineImages(
          option.lineItems ?? [],
          matches.length === 1 ? (matches[0].lineItems ?? []) : [],
        ),
      };
    }),
  };
}
